import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import useElementToPdf from '../utils/useElementToPdf';
import {
  Users,
  UserPlus,
  Phone,
  Search,
  Edit2,
  Trash2,
  Loader2,
  X,
  Save,
  Scissors,
  RefreshCw,
  Crown,
  Printer,
  TrendingUp,
  Award,
  Calendar,
} from 'lucide-react';

const emptyForm = { name: '', phone: '', preferred_barbers: [], notes: '' };

const Customers = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isArabic = language === 'ar';

  const [customers, setCustomers] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBarber, setFilterBarber] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState('top-spenders'); // top-spenders | top-visits-week | top-visits-month
  const [reportLoading, setReportLoading] = useState(false);
  const reportPrintRef = useRef(null);

  const reportTitle = (
    reportType === 'top-spenders' ? (isArabic ? 'تقرير: أعلى العملاء إنفاقاً' : 'Report: Top Spenders') :
    reportType === 'top-visits-week' ? (isArabic ? 'تقرير: أعلى زيارات هذا الأسبوع' : 'Report: Top Visits — Week') :
    (isArabic ? 'تقرير: أعلى زيارات هذا الشهر' : 'Report: Top Visits — Month')
  );

  const printReport = useElementToPdf({
    contentRef: reportPrintRef,
    documentTitle: `customers_report_${reportType}_${new Date().toISOString().slice(0,10)}`,
    pageStyle: '@page { size: A4 portrait; margin: 12mm; }',
  });

  const syncFromLoyalty = async () => {
    if (!window.confirm(isArabic ? 'سيتم استيراد كل بطاقات الولاء المعتمدة إلى سجل العملاء وتحديث الأسماء/الأرقام. هل تريد المتابعة؟' : 'Sync all approved loyalty cards into Customers?')) return;
    setSyncing(true);
    try {
      const res = await api.post('/customers/sync-from-loyalty');
      toast.success(`${res.data.message}: +${res.data.added}, ↻${res.data.updated}, ⊘${res.data.skipped}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشلت المزامنة' : 'Sync failed'));
    } finally {
      setSyncing(false);
    }
  };

  const loadReport = async (type) => {
    setReportLoading(true);
    setReportType(type);
    try {
      let url = '/customers/reports/top-spenders';
      if (type === 'top-visits-week') url = '/customers/reports/top-visits?period=week';
      if (type === 'top-visits-month') url = '/customers/reports/top-visits?period=month';
      const res = await api.get(url);
      setReportData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل تحميل التقرير' : 'Failed'));
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, bRes] = await Promise.all([
        api.get('/customers', { params: { q: search || undefined, barber_id: filterBarber || undefined } }),
        api.get('/barbers/all').catch(() => api.get('/barbers')),
      ]);
      setCustomers(cRes.data || []);
      setBarbers(bRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل التحميل' : 'Failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarber]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    fetchAll();
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '',
      phone: c.phone || '',
      preferred_barbers: c.preferred_barbers || [],
      notes: c.notes || '',
    });
    setShowModal(true);
  };

  const togglePreferred = (id) => {
    setForm((f) => ({
      ...f,
      preferred_barbers: f.preferred_barbers.includes(id)
        ? f.preferred_barbers.filter((x) => x !== id)
        : [...f.preferred_barbers, id],
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(isArabic ? 'الاسم مطلوب' : 'Name required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success(isArabic ? 'تم التحديث' : 'Updated');
      } else {
        await api.post('/customers', form);
        toast.success(isArabic ? 'تم الإضافة' : 'Added');
      }
      setShowModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل الحفظ' : 'Failed'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(isArabic ? `حذف ${c.name}؟` : `Delete ${c.name}?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success(isArabic ? 'تم الحذف' : 'Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل الحذف' : 'Failed'));
    }
  };

  const incrementVisit = async (c) => {
    try {
      await api.post(`/customers/${c.id}/visit`);
      toast.success(isArabic ? 'تم تسجيل الزيارة' : 'Visit recorded');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const barbersById = useMemo(() => {
    const m = {};
    for (const b of barbers) m[b.id] = b;
    return m;
  }, [barbers]);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{isArabic ? 'سجل العملاء الكرام' : 'Esteemed Customers'}</h1>
            <p className="text-muted-foreground text-sm">
              {isArabic ? 'إحصاءات الزيارات والإنفاق، مزامنة مع بطاقات الولاء، وتقارير قابلة للطباعة' : 'Visits & spending stats, loyalty sync, printable reports'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user?.role === 'admin' && (
            <>
              <Button
                onClick={syncFromLoyalty}
                variant="outline"
                className="gap-2"
                data-testid="sync-from-loyalty-btn"
                disabled={syncing}
              >
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                {isArabic ? 'مزامنة من الولاء' : 'Sync from Loyalty'}
              </Button>
              <Button
                onClick={() => setShowReports(true)}
                variant="outline"
                className="gap-2"
                data-testid="open-reports-btn"
              >
                <Printer className="w-4 h-4" />
                {isArabic ? 'التقارير' : 'Reports'}
              </Button>
            </>
          )}
          <Button onClick={openAdd} className="gap-2" data-testid="add-customer-btn">
            <UserPlus className="w-4 h-4" />
            {isArabic ? 'إضافة عميل' : 'Add Customer'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 bg-card border-white/10">
        <form onSubmit={onSearchSubmit} className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isArabic ? 'بحث بالاسم أو رقم الجوال...' : 'Search by name or phone...'}
              className="ps-10 bg-secondary/50 border-white/10"
              data-testid="customer-search-input"
            />
          </div>
          <select
            value={filterBarber}
            onChange={(e) => setFilterBarber(e.target.value)}
            className="h-10 px-3 rounded-md bg-secondary/50 border border-white/10 min-w-[180px]"
            data-testid="customer-barber-filter"
          >
            <option value="">{isArabic ? 'كل الحلاقين' : 'All Barbers'}</option>
            {barbers.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <Button type="submit" variant="outline" className="gap-1">
            <Search className="w-4 h-4" />
            {isArabic ? 'بحث' : 'Search'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="gap-1"
            onClick={() => { setSearch(''); setFilterBarber(''); setTimeout(fetchAll, 0); }}
          >
            <RefreshCw className="w-4 h-4" />
            {isArabic ? 'تحديث' : 'Reset'}
          </Button>
        </form>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden bg-card border-white/10">
        {loading ? (
          <div className="text-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline-block me-2" />
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
            {isArabic ? 'لا يوجد زبائن مطابقون' : 'No customers found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-start py-3 px-3 font-semibold">{isArabic ? 'الاسم' : 'Name'}</th>
                  <th className="text-start py-3 px-3 font-semibold">{isArabic ? 'الجوال' : 'Phone'}</th>
                  <th className="text-start py-3 px-3 font-semibold">{isArabic ? 'العضوية' : 'Membership'}</th>
                  <th className="text-start py-3 px-3 font-semibold whitespace-nowrap">{isArabic ? 'التسجيل' : 'Joined'}</th>
                  <th className="text-start py-3 px-3 font-semibold whitespace-nowrap">{isArabic ? 'آخر زيارة' : 'Last visit'}</th>
                  <th className="text-center py-3 px-3 font-semibold">{isArabic ? 'الزيارات' : 'Visits'}</th>
                  <th className="text-end py-3 px-3 font-semibold">{isArabic ? 'متوسط الإنفاق' : 'Avg Spend'}</th>
                  <th className="text-end py-3 px-3 font-semibold">{isArabic ? 'إجمالي الإنفاق' : 'Total Spent'}</th>
                  <th className="text-center py-3 px-3 font-semibold whitespace-nowrap">{isArabic ? 'النقاط (رصيد)' : 'Points (balance)'}</th>
                  <th className="text-center py-3 px-3 font-semibold whitespace-nowrap">{isArabic ? 'نقاط مُستبدَلة' : 'Redeemed'}</th>
                  <th className="text-start py-3 px-3 font-semibold">{isArabic ? 'حصص الحلاقين' : 'Barbers Share'}</th>
                  <th className="text-center py-3 px-3 font-semibold">{isArabic ? 'إجراء' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-t border-white/5 hover:bg-secondary/20" data-testid={`customer-row-${c.id}`}>
                    <td className="py-3 px-3 font-medium">
                      <div>{c.name}</div>
                      {c.membership_joined_at && (
                        <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {isArabic ? 'منذ' : 'since'} {c.membership_joined_at.substring(0, 10)}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-muted-foreground">
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                          <Phone className="w-3 h-3" />
                          {c.phone}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-3">
                      {c.loyalty_membership_number ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-xs font-mono font-bold">
                          <Crown className="w-3 h-3" />
                          #{c.loyalty_membership_number}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-muted-foreground text-xs whitespace-nowrap" data-testid={`customer-joined-${c.id}`}>
                      {(() => {
                        const v = c.membership_joined_at || c.created_at;
                        if (!v) return '—';
                        const d = new Date(v);
                        if (isNaN(d.getTime())) return '—';
                        const iso = new Intl.DateTimeFormat('en-CA', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Dubai' }).format(d);
                        const [y, m, dd] = iso.split('-');
                        return `${dd}/${m}/${y}`;
                      })()}
                    </td>
                    <td className="py-3 px-3 text-muted-foreground text-xs whitespace-nowrap" data-testid={`customer-last-visit-${c.id}`}>
                      {(() => {
                        const v = c.last_visit_at;
                        if (!v) return '—';
                        const d = new Date(v);
                        if (isNaN(d.getTime())) return '—';
                        const iso = new Intl.DateTimeFormat('en-CA', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Dubai' }).format(d);
                        const [y, m, dd] = iso.split('-');
                        return `${dd}/${m}/${y}`;
                      })()}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <button
                        type="button"
                        onClick={() => incrementVisit(c)}
                        title={isArabic ? 'تسجيل زيارة جديدة' : 'Record a visit'}
                        className="inline-flex items-center justify-center min-w-[36px] h-7 px-2 rounded-full bg-emerald-500/15 text-emerald-500 text-xs font-bold hover:bg-emerald-500/25"
                        data-testid={`visit-btn-${c.id}`}
                      >
                        {c.visit_count_revenues || c.visit_count || 0}
                      </button>
                    </td>
                    <td className="py-3 px-3 text-end font-mono">
                      {(c.avg_spent_aed || 0).toFixed(0)} د.إ
                    </td>
                    <td className="py-3 px-3 text-end font-mono font-bold text-primary">
                      {(c.total_spent_aed || 0).toFixed(0)} د.إ
                    </td>
                    <td className="py-3 px-3 text-center" data-testid={`customer-points-${c.id}`}>
                      {(c.loyalty_points_balance || 0) > 0 ? (
                        <div className="inline-flex flex-col items-center leading-tight">
                          <span className="text-amber-400 font-bold text-sm">
                            {c.loyalty_points_balance}
                          </span>
                          <span className="text-[10px] text-muted-foreground font-mono">
                            ≈ {(c.loyalty_points_balance_aed || 0).toFixed(2)} د.إ
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center" data-testid={`customer-redeemed-${c.id}`}>
                      {(c.loyalty_points_redeemed || 0) > 0 ? (
                        <div className="inline-flex flex-col items-center leading-tight">
                          <span className="text-blue-400 font-bold text-sm">
                            {c.loyalty_points_redeemed}
                          </span>
                          <span className="text-[10px] text-muted-foreground font-mono">
                            ≈ {(c.loyalty_points_redeemed_aed || 0).toFixed(2)} د.إ
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      {(c.barbers_share || []).length === 0 ? (
                        <span className="text-muted-foreground text-xs">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {c.barbers_share.slice(0, 3).map((s) => (
                            <span
                              key={s.barber_id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-primary/15 text-primary"
                              title={`${s.visits} زيارة - ${s.amount} د.إ`}
                            >
                              <Scissors className="w-3 h-3" />
                              {s.barber_name} {s.percent}%
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {(user?.role === 'admin' || c.created_by === user?.id) && (
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => remove(c)} data-testid={`delete-customer-${c.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="customer-modal">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-md w-full space-y-4" dir={isArabic ? 'rtl' : 'ltr'}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {editing ? (isArabic ? 'تعديل الزبون' : 'Edit Customer') : (isArabic ? 'زبون جديد' : 'New Customer')}
              </h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{isArabic ? 'الاسم *' : 'Name *'}</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-secondary/50 border-white/10"
                data-testid="customer-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{isArabic ? 'الجوال' : 'Phone'}</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+9715XXXXXXXX"
                className="bg-secondary/50 border-white/10"
                data-testid="customer-phone-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {isArabic ? 'الحلاق المفضّل (يمكن اختيار أكثر من واحد)' : 'Preferred Barber (multi-select)'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {barbers.map((b) => (
                  <label
                    key={b.id}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border transition-colors ${
                      form.preferred_barbers.includes(b.id)
                        ? 'bg-primary/15 border-primary'
                        : 'bg-secondary/30 border-white/10 hover:bg-secondary/50'
                    }`}
                  >
                    <Checkbox
                      checked={form.preferred_barbers.includes(b.id)}
                      onCheckedChange={() => togglePreferred(b.id)}
                    />
                    <span className="text-sm">{b.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{isArabic ? 'ملاحظات' : 'Notes'}</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full rounded-md bg-secondary/50 border border-white/10 px-3 py-2 text-sm"
                data-testid="customer-notes-input"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={submit} disabled={saving} className="gap-1" data-testid="save-customer-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isArabic ? 'حفظ' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reports Modal */}
      {showReports && user?.role === 'admin' && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto print:bg-white print:p-0 print:overflow-visible">
          <div
            className="bg-card border border-white/10 rounded-2xl p-6 max-w-3xl w-full mx-auto my-8 print:bg-white print:text-black print:border-0 print:max-w-full print:my-0 print:p-4 print:rounded-none"
            dir={isArabic ? 'rtl' : 'ltr'}
          >
            <div className="flex items-center justify-between mb-4 print:hidden">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                {isArabic ? 'تقارير العملاء الكرام' : 'Customers Reports'}
              </h3>
              <button onClick={() => { setShowReports(false); setReportData(null); }} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Report selector */}
            <div className="flex flex-wrap gap-2 mb-4 print:hidden">
              <Button
                size="sm"
                variant={reportType === 'top-spenders' ? 'default' : 'outline'}
                onClick={() => loadReport('top-spenders')}
                className="gap-1"
                data-testid="report-top-spenders"
              >
                <Award className="w-4 h-4" />
                {isArabic ? 'أعلى إنفاق' : 'Top Spenders'}
              </Button>
              <Button
                size="sm"
                variant={reportType === 'top-visits-week' ? 'default' : 'outline'}
                onClick={() => loadReport('top-visits-week')}
                className="gap-1"
                data-testid="report-top-visits-week"
              >
                <Calendar className="w-4 h-4" />
                {isArabic ? 'أعلى زيارات (أسبوع)' : 'Top Visits (Week)'}
              </Button>
              <Button
                size="sm"
                variant={reportType === 'top-visits-month' ? 'default' : 'outline'}
                onClick={() => loadReport('top-visits-month')}
                className="gap-1"
                data-testid="report-top-visits-month"
              >
                <Calendar className="w-4 h-4" />
                {isArabic ? 'أعلى زيارات (شهر)' : 'Top Visits (Month)'}
              </Button>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="outline"
                onClick={printReport}
                disabled={!reportData || reportLoading}
                className="gap-1"
                data-testid="print-report-btn"
              >
                <Printer className="w-4 h-4" />
                {isArabic ? 'تنزيل PDF' : 'Download PDF'}
              </Button>
            </div>

            {/* Print header */}
            <div className="hidden print:block mb-4 text-center">
              <h1 className="text-2xl font-bold mb-1">KE Gents Saloon</h1>
              <p className="text-sm">
                {reportType === 'top-spenders' && (isArabic ? 'تقرير: أعلى العملاء إنفاقاً' : 'Report: Top Spenders')}
                {reportType === 'top-visits-week' && (isArabic ? 'تقرير: أعلى زيارات هذا الأسبوع' : 'Report: Top Visits — Week')}
                {reportType === 'top-visits-month' && (isArabic ? 'تقرير: أعلى زيارات هذا الشهر' : 'Report: Top Visits — Month')}
              </p>
              <p className="text-xs">{new Date().toLocaleString(isArabic ? 'ar-AE' : 'en-GB')}</p>
            </div>

            {/* Body */}
            {reportLoading ? (
              <div className="text-center py-10 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                {isArabic ? 'جاري التحميل...' : 'Loading...'}
              </div>
            ) : !reportData ? (
              <div className="text-center py-10 text-muted-foreground">
                {isArabic ? 'اختر تقريراً من الأعلى لعرضه.' : 'Pick a report above to view.'}
              </div>
            ) : reportData.items.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                {isArabic ? 'لا توجد بيانات لهذه الفترة' : 'No data for this period'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm print:text-xs" data-testid="report-table">
                  <thead className="bg-secondary/40 print:bg-gray-100">
                    <tr>
                      <th className="text-start py-2 px-3">#</th>
                      <th className="text-start py-2 px-3">{isArabic ? 'الاسم' : 'Name'}</th>
                      <th className="text-start py-2 px-3">{isArabic ? 'الجوال' : 'Phone'}</th>
                      <th className="text-start py-2 px-3">{isArabic ? 'العضوية' : 'Membership'}</th>
                      <th className="text-center py-2 px-3">{isArabic ? 'الزيارات' : 'Visits'}</th>
                      <th className="text-end py-2 px-3">{isArabic ? 'الإنفاق' : 'Spent'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.items.map((row, idx) => (
                      <tr key={row.membership_number} className="border-t border-white/5 print:border-gray-200">
                        <td className="py-2 px-3 font-bold">{idx + 1}</td>
                        <td className="py-2 px-3">{row.name || '—'}</td>
                        <td className="py-2 px-3 font-mono text-xs" dir="ltr">{row.phone || '—'}</td>
                        <td className="py-2 px-3 font-mono text-amber-400 print:text-black">#{row.membership_number}</td>
                        <td className="py-2 px-3 text-center font-bold">{row.visits}</td>
                        <td className="py-2 px-3 text-end font-mono font-bold text-primary print:text-black">
                          {Number(row.total_spent_aed || 0).toFixed(0)} د.إ
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[11px] text-muted-foreground print:text-gray-600 mt-3 text-center">
                  {isArabic ? 'إجمالي السجلات' : 'Total records'}: {reportData.count}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Off-screen printable report sheet (for real PDF download) */}
      {reportData && reportData.items && reportData.items.length > 0 && (
        <div
          aria-hidden="true"
          style={{ position: 'fixed', left: '-10000px', top: 0, width: '210mm', pointerEvents: 'none' }}
        >
          <div
            ref={reportPrintRef}
            dir={isArabic ? 'rtl' : 'ltr'}
            style={{
              width: '210mm',
              minHeight: '297mm',
              padding: '14mm',
              background: '#fff',
              color: '#111',
              fontFamily: 'Cairo, Tajawal, Arial, sans-serif',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <h1 style={{ margin: 0, fontSize: 22 }}>KE Gents Saloon</h1>
              <h2 style={{ margin: '6px 0', fontSize: 14 }}>{reportTitle}</h2>
              <p style={{ margin: 0, fontSize: 11, color: '#555' }}>
                {new Date().toLocaleString(isArabic ? 'ar-AE' : 'en-GB')}
              </p>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f3f4f6' }}>
                  <th style={{ padding: 6, border: '1px solid #ddd', textAlign: isArabic ? 'right' : 'left' }}>#</th>
                  <th style={{ padding: 6, border: '1px solid #ddd', textAlign: isArabic ? 'right' : 'left' }}>{isArabic ? 'الاسم' : 'Name'}</th>
                  <th style={{ padding: 6, border: '1px solid #ddd', textAlign: isArabic ? 'right' : 'left' }}>{isArabic ? 'الجوال' : 'Phone'}</th>
                  <th style={{ padding: 6, border: '1px solid #ddd', textAlign: isArabic ? 'right' : 'left' }}>{isArabic ? 'العضوية' : 'Membership'}</th>
                  <th style={{ padding: 6, border: '1px solid #ddd', textAlign: 'center' }}>{isArabic ? 'الزيارات' : 'Visits'}</th>
                  <th style={{ padding: 6, border: '1px solid #ddd', textAlign: isArabic ? 'left' : 'right' }}>{isArabic ? 'الإنفاق' : 'Spent'}</th>
                </tr>
              </thead>
              <tbody>
                {reportData.items.map((row, idx) => (
                  <tr key={row.membership_number}>
                    <td style={{ padding: 6, border: '1px solid #eee', fontWeight: 700 }}>{idx + 1}</td>
                    <td style={{ padding: 6, border: '1px solid #eee' }}>{row.name || '—'}</td>
                    <td style={{ padding: 6, border: '1px solid #eee', fontFamily: 'monospace' }} dir="ltr">{row.phone || '—'}</td>
                    <td style={{ padding: 6, border: '1px solid #eee', fontFamily: 'monospace' }}>#{row.membership_number}</td>
                    <td style={{ padding: 6, border: '1px solid #eee', textAlign: 'center', fontWeight: 700 }}>{row.visits}</td>
                    <td style={{ padding: 6, border: '1px solid #eee', textAlign: isArabic ? 'left' : 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                      {Number(row.total_spent_aed || 0).toFixed(0)} د.إ
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 14, textAlign: 'center', fontSize: 11, color: '#555' }}>
              {isArabic ? 'إجمالي السجلات' : 'Total records'}: {reportData.count}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
